import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { useState, useEffect, useCallback } from "react";
import {
  Loader2,
  ArrowRight,
  PlusCircle,
  SquareArrowOutUpRight,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { logger } from "@/services/logger";
import { regionCoordinates } from "./interactive-map";
import axios from "axios";

// Type definition for regionCoordinates to allow string indexing
type RegionCoordinatesType = Record<
  string,
  {
    postalCode: string;
    address: string;
    lat?: number;
    lng?: number;
  }
>;

// Cast regionCoordinates to the new type
const typedRegionCoordinates = regionCoordinates as RegionCoordinatesType;

const quoteFormSchema = z.object({
  pickupAddress: z.object({
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
    postalCode: z.string(),
  }),
  deliveryAddress: z.object({
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
    postalCode: z.string(),
  }),
  parcels: z.array(
    z.object({
      weight: z.number().min(0),
      dimensions: z.object({
        length: z.number().min(0),
        width: z.number().min(0),
        height: z.number().min(0),
      }),
    })
  ),
});

interface QuoteResponse {
  tierName: string;
  shippingOptions: {
    speedType: string;
    price: number;
    speedDescription?: string;
  }[];
}

type QuoteFormValues = z.infer<typeof quoteFormSchema>;

interface QuoteFormProps {
  selectedLocations?: {
    from: string | null;
    to: string | null;
  };
}

export function QuoteForm({ selectedLocations }: QuoteFormProps) {
  const [quotes, setQuotes] = useState<QuoteResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      pickupAddress: {
        location: { lat: 43.6534, lng: -79.3841 }, // Default to Toronto coordinates
        postalCode: "",
      },
      deliveryAddress: {
        location: { lat: 43.6534, lng: -79.3841 }, // Default to Toronto coordinates
        postalCode: "",
      },
      parcels: [
        {
          weight: 2,
          dimensions: {
            length: 30,
            width: 30,
            height: 30,
          },
        },
      ],
    },
  });

  const onSubmit = useCallback(async (values: QuoteFormValues) => {
    setIsLoading(true);
    try {
      const response = await axios.post<QuoteResponse[]>(
        `${import.meta.env.VITE_API_URL}/api/v1/pricing/quotes`,
        {
          ...values,
          customerTiers: ["Iron", "Bronze", "Silver", "Gold", "Platinum"],
        }
      );
      setQuotes(response.data);
    } catch (error) {
      logger.error("Error fetching quotes:", error);

      // Set empty quotes if request fails
      setQuotes([]);
    } finally {
      setIsLoading(false);
      setShouldAutoSubmit(false);
    }
  }, []);

  // Simple effect to update addresses when locations change
  useEffect(() => {
    // Track if both addresses were set
    let bothAddressesSet = false;

    if (
      selectedLocations?.from &&
      typedRegionCoordinates[selectedLocations.from]
    ) {
      const fromCoords = typedRegionCoordinates[selectedLocations.from];
      form.setValue("pickupAddress", {
        location: {
          lat: 43.6534, // Default to Toronto coordinates
          lng: -79.3841,
        },
        postalCode: fromCoords.postalCode || "",
      });

      if (
        selectedLocations.to &&
        typedRegionCoordinates[selectedLocations.to]
      ) {
        bothAddressesSet = true;
      }
    }

    if (selectedLocations?.to && typedRegionCoordinates[selectedLocations.to]) {
      const toCoords = typedRegionCoordinates[selectedLocations.to];
      form.setValue("deliveryAddress", {
        location: {
          lat: 43.6534, // Default to Toronto coordinates
          lng: -79.3841,
        },
        postalCode: toCoords.postalCode || "",
      });

      if (
        selectedLocations.from &&
        typedRegionCoordinates[selectedLocations.from]
      ) {
        bothAddressesSet = true;
      }
    }

    if (bothAddressesSet) {
      setShouldAutoSubmit(true);
    }
  }, [selectedLocations, form]);

  // Separate effect to handle auto-submission
  useEffect(() => {
    if (shouldAutoSubmit) {
      const timer = setTimeout(() => {
        const values = form.getValues();
        void onSubmit(values);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shouldAutoSubmit, form, onSubmit]);

  const getTierStyles = (tierName: string) => {
    switch (tierName) {
      case "Iron":
        return {
          border: "border-gray-400",
          text: "text-gray-600",
          badge: "bg-gray-400 text-white hover:bg-gray-400",
        };
      case "Bronze":
        return {
          border: "border-amber-600",
          text: "text-amber-700",
          badge: "bg-amber-600 text-white hover:bg-amber-600",
        };
      case "Silver":
        return {
          border: "border-slate-400",
          text: "text-slate-600",
          badge: "bg-slate-400 text-white hover:bg-slate-400",
        };
      case "Gold":
        return {
          border: "border-yellow-500",
          text: "text-yellow-600",
          badge: "bg-yellow-500 text-black hover:bg-yellow-500",
        };
      case "Platinum":
        return {
          border: "border-cyan-500",
          text: "text-cyan-600",
          badge: "bg-cyan-500 text-black hover:bg-cyan-500",
        };
      case "Diamond":
        return {
          border: "border-purple-500",
          text: "text-purple-600",
          badge: "bg-purple-500 text-black hover:bg-purple-500",
        };
      default:
        return {
          border: "border-gray-200",
          text: "text-gray-700",
          badge: "",
        };
    }
  };

  return (
    <Form {...form}>
      <div className="flex flex-col md:flex-row gap-8 max-w-[1200px] w-full mx-auto">
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-8"
        >
          {/* Add immediate submit button at the top when addresses are set */}
          {selectedLocations?.from && selectedLocations?.to && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-purple-700">
                    Ready to calculate your quote?
                  </h3>
                  <p className="text-sm text-purple-600">
                    Both pickup and delivery locations are set
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    const values = form.getValues();
                    void onSubmit(values);
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Calculate Now
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-8">
            {/* Address Section */}
            <div className="flex-1 space-y-6">
              <div className="-mb-3">
                <p className="text-xl font-semibold">Address</p>
                <p className="text-sm text-gray-500">
                  Where do you ship from and to?
                </p>
              </div>
              <Separator />
              <div className="space-y-4">
                <FormLabel>Pickup Address</FormLabel>
                <AutocompleteAddress
                  onSelectedAddress={(address) => {
                    if (address) {
                      form.setValue("pickupAddress", {
                        location: {
                          lat: address.location?.lat || 43.6534, // Default to Toronto coordinates if missing
                          lng: address.location?.lng || -79.3841,
                        },
                        postalCode: address.addressComponents.postalCode || "",
                      });
                    }
                  }}
                  placeholder="Enter pickup address"
                  onlyShowAutocomplete={true}
                  initialValue={
                    selectedLocations?.from &&
                    typedRegionCoordinates[selectedLocations.from]
                      ? typedRegionCoordinates[selectedLocations.from].address
                      : undefined
                  }
                />
              </div>
              <div className="space-y-4">
                <FormLabel>Delivery Address</FormLabel>
                <AutocompleteAddress
                  onSelectedAddress={(address) => {
                    if (address) {
                      form.setValue("deliveryAddress", {
                        location: {
                          lat: address.location?.lat || 43.6534, // Default to Toronto coordinates if missing
                          lng: address.location?.lng || -79.3841,
                        },
                        postalCode: address.addressComponents.postalCode || "",
                      });
                    }
                  }}
                  placeholder="Enter delivery address"
                  onlyShowAutocomplete={true}
                  initialValue={
                    selectedLocations?.to &&
                    typedRegionCoordinates[selectedLocations.to]
                      ? typedRegionCoordinates[selectedLocations.to].address
                      : undefined
                  }
                />
              </div>
            </div>

            {/* Parcel Section with Button */}
            <div className="flex-1 space-y-6">
              <div className="-mb-3">
                <p className="text-xl font-semibold">Parcel</p>
                <p className="text-sm text-gray-500">
                  What's the parcel's dimension & weight?
                </p>
              </div>
              <Separator />
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="parcels.0.dimensions.length"
                  render={({ field }) => (
                    <FormItem className="space-y-4">
                      <FormLabel>Length (cm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="parcels.0.dimensions.width"
                  render={({ field }) => (
                    <FormItem className="space-y-4">
                      <FormLabel>Width (cm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="parcels.0.dimensions.height"
                  render={({ field }) => (
                    <FormItem className="space-y-4">
                      <FormLabel>Height (cm)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="parcels.0.weight"
                render={({ field }) => (
                  <FormItem className="space-y-4">
                    <FormLabel>Weight (kg)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* New Info Card for Parcels */}
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-md font-medium">
                    Sign up to get pricings for multiple parcels in one order
                  </p>
                  <SquareArrowOutUpRight className="w-4 h-4" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight className="w-8 h-8 hidden md:block text-gray-400" />
            </div>
          </div>

          {/* Moved Calculate Button */}
          <div className="flex justify-center lg:mr-16 lg:mt-20">
            <Button
              type="submit"
              disabled={isLoading}
              size="lg"
              className="w-full lg:w-[400px]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                "Calculate Quote"
              )}
            </Button>
          </div>
        </form>

        {/* Options Section */}
        <div className="w-full md:w-[400px]">
          <div className="mb-3 flex items-center gap-2">
            <div>
              <p className="text-xl font-semibold">Options</p>
              <p className="text-sm text-gray-500">
                Choose one of the options below
              </p>
            </div>
          </div>
          <Separator className="mb-6" />
          {quotes.length > 0 ? (
            <div className="space-y-6">
              {quotes
                .filter((quote) => quote.shippingOptions.length > 0)
                .map((quote, index) => {
                  const styles = getTierStyles(quote.tierName);
                  return (
                    <div
                      key={index}
                      className={`p-6 rounded-xl border-2 transition-all relative ${styles.border} hover:border-opacity-80`}
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge
                          variant="secondary"
                          className={`whitespace-nowrap ${styles.badge}`}
                        >
                          {quote.tierName}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-start">
                        <div className="w-full">
                          {quote.shippingOptions.map((option, optIndex) => (
                            <div key={optIndex} className="py-2">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className={`font-medium ${styles.text}`}>
                                    {option.speedType.replace("_", " ")}
                                  </p>
                                  {option.speedDescription && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {option.speedDescription}
                                    </p>
                                  )}
                                </div>
                                <p className={`font-medium ${styles.text}`}>
                                  ${option.price.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : form.formState.isDirty ? (
            <div className="p-6 rounded-xl border-2 border-gray-200 text-center">
              <p className="text-gray-500">No shipping options available</p>
            </div>
          ) : (
            <div className="p-6 rounded-xl border-2 border-gray-200 text-center">
              <p className="text-gray-500">
                Please fill in the form to get a quote
              </p>
            </div>
          )}

          {/* New Info Card */}
          <div className="mt-6 p-4 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium">Want to learn more about pricing?</p>
              <PlusCircle className="w-4 h-4" />
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Explore the differences between free and pro tier.
            </p>
          </div>
        </div>
      </div>
    </Form>
  );
}
